import {
  BulkDeleteAssetsRequest,
  BulkDeleteAssetsRequestAsset
} from '../../../models/image/requests/bulk-delete-assets-request';
import { MediaManagerAssetFolderDto } from '../../../models/image/dto/media-manager-asset-folder-dto';
import { MediaManagerAssetLibraryDto } from '../../../models/image/dto/media-manager-asset-library-dto';
import { applyDeletedAssets } from './asset-patch';
import { applyDeletedFolder } from './folder-patch';
import { findAssetInLibrary } from './asset-library-lookups';
import { MediaManagerFileSystem } from 'src/app/models/media-manager/media-manager-file-system';

/**
 * Build the WebSocket bulk-delete payload from a selection of asset unique ids and folder ids. Assets are
 * translated back to their `id` + `md5Hash` pair because that is what the delete API keys on.
 */
export function buildBulkDeleteAssetsRequest(
  assetUniqueIds: string[],
  folderIds: string[],
  targetFolderId: string,
  deleteAllItems: boolean,
  assetLibrary: MediaManagerAssetLibraryDto
): BulkDeleteAssetsRequest {
  const assets = assetUniqueIds.map(assetUniqueId => {
    const asset = findAssetInLibrary(assetUniqueId, assetLibrary);
    return new BulkDeleteAssetsRequestAsset(asset?.id, asset?.md5Hash);
  });
  return new BulkDeleteAssetsRequest(assets, folderIds, deleteAllItems ? '' : targetFolderId || 'ROOT', deleteAllItems);
}

/**
 * Apply an accepted bulk-delete optimistically to the in-memory state. Assets the user picked out by hand are
 * removed outright before folders are collapsed so folder contents do not get "moved" into targets they were
 * meant to be deleted from.
 *
 * @param assetUniqueIds the library's `id-md5Hash` keys for request.assets, which the request itself cannot carry.
 */
export function applyBulkDeletedItems(
  assetUniqueIds: string[],
  request: BulkDeleteAssetsRequest,
  filesystem: MediaManagerFileSystem,
  assetLibrary: MediaManagerAssetLibraryDto
): boolean {
  const existingAssetUniqueIds = assetUniqueIds.filter(assetUniqueId => filesystem.assetExists(assetUniqueId));
  if (existingAssetUniqueIds.length && !applyDeletedAssets(existingAssetUniqueIds, filesystem, assetLibrary)) {
    return false;
  }
  // The REST move returns the updated target folder to patch from, but an acknowledgment carries no payload,
  // so the local move is rebuilt entirely from what is already in memory.
  const noResponseToPatchFrom = new MediaManagerAssetFolderDto();
  const existingFolderIds = request.assetFolderIds.filter(folderId => filesystem.folderExists(folderId));
  for (const folderId of existingFolderIds) {
    const folderPatchSucceeded = applyDeletedFolder(
      folderId,
      request.targetFolderId,
      request.deleteAllItems,
      noResponseToPatchFrom,
      filesystem,
      assetLibrary
    );
    if (!folderPatchSucceeded) return false;
  }
  return true;
}
